import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getWebAnalyticsSummary,
  getWebAnalyticsPageDetails,
  getWebAnalyticsCountries,
  getWebAnalyticsCountryDetails,
  getWebAnalyticsBrowsers,
} from '../../services/(adminapi)/webAnalyticsAPI';

const emptySummary = {
  page_views: 0,
  events: 0,
  unique_visitors: 0,
  sessions: 0,
  comparison_available: false,
  previous: {
    page_views: 0,
    events: 0,
    unique_visitors: 0,
    sessions: 0,
  },
  changes: null,
};

const emptyPageDetails = {
  path: null,
  title: null,
  url: null,
  days: 30,
  summary: {
    page_views: 0,
    unique_visitors: 0,
    sessions: 0,
    events: 0,
    avg_time_on_page_ms: null,
    avg_scroll_depth: null,
    bounce_rate: 0,
  },
  top_referrers: [],
  by_device: [],
  event_breakdown: [],
};

export const fetchWebAnalyticsSummary = createAsyncThunk(
  'webAnalytics/fetchSummary',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await getWebAnalyticsSummary(days);
      const payload = response?.data ?? response;
      const summary = {
        ...emptySummary,
        ...(payload?.summary || {}),
        previous: {
          ...emptySummary.previous,
          ...(payload?.summary?.previous || {}),
        },
        changes: {
          ...emptySummary.changes,
          ...(payload?.summary?.changes || {}),
        },
      };
      return {
        days: payload?.days ?? days,
        summary,
        top_pages: Array.isArray(payload?.top_pages) ? payload.top_pages : [],
        top_referrers: Array.isArray(payload?.top_referrers)
          ? payload.top_referrers
          : [],
        by_device: Array.isArray(payload?.by_device) ? payload.by_device : [],
        event_breakdown: Array.isArray(payload?.event_breakdown)
          ? payload.event_breakdown
          : [],
        traffic_over_time: Array.isArray(payload?.traffic_over_time)
          ? payload.traffic_over_time
          : [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch web analytics'
      );
    }
  }
);

export const fetchWebAnalyticsCountries = createAsyncThunk(
  'webAnalytics/fetchCountries',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await getWebAnalyticsCountries(days, 10);
      const payload = response?.data ?? response;
      return {
        days: payload?.days ?? days,
        countries: Array.isArray(payload?.countries) ? payload.countries : [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch countries'
      );
    }
  }
);

export const fetchWebAnalyticsBrowsers = createAsyncThunk(
  'webAnalytics/fetchBrowsers',
  async (days = 30, { rejectWithValue }) => {
    try {
      const response = await getWebAnalyticsBrowsers(days, 10);
      const payload = response?.data ?? response;
      return {
        days: payload?.days ?? days,
        browsers: Array.isArray(payload?.browsers) ? payload.browsers : [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch browsers'
      );
    }
  }
);

export const fetchWebAnalyticsPageDetails = createAsyncThunk(
  'webAnalytics/fetchPageDetails',
  async ({ path, days = 30 }, { rejectWithValue }) => {
    try {
      const response = await getWebAnalyticsPageDetails(path, days);
      const payload = response?.data ?? response;
      return {
        ...emptyPageDetails,
        ...payload,
        summary: {
          ...emptyPageDetails.summary,
          ...(payload?.summary || {}),
        },
        top_referrers: Array.isArray(payload?.top_referrers)
          ? payload.top_referrers
          : [],
        by_device: Array.isArray(payload?.by_device) ? payload.by_device : [],
        event_breakdown: Array.isArray(payload?.event_breakdown)
          ? payload.event_breakdown
          : [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch page details'
      );
    }
  }
);

export const fetchWebAnalyticsCountryDetails = createAsyncThunk(
  'webAnalytics/fetchCountryDetails',
  async ({ countryCode, days = 30 }, { rejectWithValue }) => {
    try {
      const response = await getWebAnalyticsCountryDetails(countryCode, { days });
      const payload = response?.data ?? response;
      return payload;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch country details'
      );
    }
  }
);

const webAnalyticsSlice = createSlice({
  name: 'webAnalytics',
  initialState: {
    days: 30,
    summary: emptySummary,
    topPages: [],
    topReferrers: [],
    byDevice: [],
    topCountries: [],
    byBrowser: [],
    eventBreakdown: [],
    trafficOverTime: [],
    loading: false,
    countriesLoading: false,
    browsersLoading: false,
    error: null,
    pageDetails: null,
    pageDetailsLoading: false,
    pageDetailsError: null,
    countryDetails: null,
    countryDetailsLoading: false,
    countryDetailsError: null,
  },
  reducers: {
    clearWebAnalyticsError: (state) => {
      state.error = null;
    },
    clearPageDetails: (state) => {
      state.pageDetails = null;
      state.pageDetailsError = null;
      state.pageDetailsLoading = false;
    },
    clearCountryDetails: (state) => {
      state.countryDetails = null;
      state.countryDetailsError = null;
      state.countryDetailsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWebAnalyticsSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWebAnalyticsSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.days = action.payload.days;
        state.summary = action.payload.summary;
        state.topPages = action.payload.top_pages;
        state.topReferrers = action.payload.top_referrers;
        state.byDevice = action.payload.by_device;
        state.eventBreakdown = action.payload.event_breakdown;
        state.trafficOverTime = action.payload.traffic_over_time;
      })
      .addCase(fetchWebAnalyticsSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch web analytics';
      })
      .addCase(fetchWebAnalyticsCountries.pending, (state) => {
        state.countriesLoading = true;
      })
      .addCase(fetchWebAnalyticsCountries.fulfilled, (state, action) => {
        state.countriesLoading = false;
        state.topCountries = action.payload.countries;
      })
      .addCase(fetchWebAnalyticsCountries.rejected, (state) => {
        state.countriesLoading = false;
      })
      .addCase(fetchWebAnalyticsBrowsers.pending, (state) => {
        state.browsersLoading = true;
      })
      .addCase(fetchWebAnalyticsBrowsers.fulfilled, (state, action) => {
        state.browsersLoading = false;
        state.byBrowser = action.payload.browsers;
      })
      .addCase(fetchWebAnalyticsBrowsers.rejected, (state) => {
        state.browsersLoading = false;
      })
      .addCase(fetchWebAnalyticsPageDetails.pending, (state) => {
        state.pageDetailsLoading = true;
        state.pageDetailsError = null;
      })
      .addCase(fetchWebAnalyticsPageDetails.fulfilled, (state, action) => {
        state.pageDetailsLoading = false;
        state.pageDetails = action.payload;
      })
      .addCase(fetchWebAnalyticsPageDetails.rejected, (state, action) => {
        state.pageDetailsLoading = false;
        state.pageDetailsError =
          action.payload || 'Failed to fetch page details';
      })
      .addCase(fetchWebAnalyticsCountryDetails.pending, (state) => {
        state.countryDetailsLoading = true;
        state.countryDetailsError = null;
      })
      .addCase(fetchWebAnalyticsCountryDetails.fulfilled, (state, action) => {
        state.countryDetailsLoading = false;
        state.countryDetails = action.payload;
      })
      .addCase(fetchWebAnalyticsCountryDetails.rejected, (state, action) => {
        state.countryDetailsLoading = false;
        state.countryDetailsError =
          action.payload || 'Failed to fetch country details';
      });
  },
});

export const {
  clearWebAnalyticsError,
  clearPageDetails,
  clearCountryDetails,
} = webAnalyticsSlice.actions;
export default webAnalyticsSlice.reducer;
